import { Button, IconButton, InputAdornment } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Stack } from '@mui/system';
import { SubmitHandler, DefaultValues, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import AuthContainer from './components/AuthContainer';
import { useSendOTPMutation } from '../../api/authApiSlice';
import AppTextField from '../../common/components/form-inputs/AppTextField';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import Link from '@mui/material/Link';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { OtpNature, SendType } from '@/api/enum/otpNature.enum';
import { useAppDispatch } from '@/redux-hooks';
import { setOTPEmail } from './authSlice';
import AuthCard from './components/AuthCard';

export type FormValues = {
	email: string;
};

export const defaultValues: DefaultValues<FormValues> = {
	email: '',
};

export default function ForgotPasswordPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const formSchema = Yup.object().shape({
		email: Yup.string()
			.required(t('errorMessage.required'))
			.email(t('errorMessage.email')),
	});

	const { handleSubmit, control } = useForm<FormValues>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const [sendOTP] = useSendOTPMutation();

	const onSubmit: SubmitHandler<FormValues> = async (data) => {
		const response = await sendOTP({
			nature: OtpNature.WEB_FORGET_PWD,
			email: data.email,
			sendType: SendType.EMAIL,
		});

		if ('data' in response) {
			dispatch(setOTPEmail(data.email));
			navigate(`/forgot-password/verify`);
		}
	};

	return (
		<>
			<AuthContainer>
				<AuthCard
					title={t('forgotPassword.forgotPasswordTitle')}
					message={t('forgotPassword.forgotPasswordMessage')}
					cardContentStyle={{ marginTop: '50px' }}
				>
					<Link
						color='primary'
						component={RouterLink}
						to='/login'
						position='absolute'
						top='50px'
						left='375px'
						display='flex'
						alignItems='center'
						sx={{
							textDecoration: 'none',
						}}
					>
						<KeyboardArrowLeftIcon />
						{t('button.back')}
					</Link>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Stack alignItems='center' spacing='24px' marginTop='40px'>
							<AppTextField
								control={control}
								name='email'
								label={t('common.email')}
								InputProps={{
									endAdornment: (
										<InputAdornment position='end'>
											<IconButton
												aria-label='toggle password visibility'
												edge='end'
												sx={{ pointerEvents: 'none' }}
											>
												<EmailOutlinedIcon />
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						</Stack>
						<Box sx={{ display: 'flex', justifyContent: 'center' }}>
							<Button
								fullWidth
								type='submit'
								variant='contained'
								sx={{
									width: '240px',
									marginTop: '40px',
									bgcolor: 'transparent',
									background: 'linear-gradient(to right, #FBCC4E, #FC9381) ',
								}}
							>
								{t('button.sendVerificationEmail')}
							</Button>
						</Box>
					</form>
				</AuthCard>
			</AuthContainer>
		</>
	);
}
